import React from 'react';
import { alpha, Box, BoxProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export type PillTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type PillVariant = 'filled' | 'outlined';
export type PillShape = 'pill' | 'rounded';

export interface PillProps extends BoxProps {
  shape?: PillShape;
  tone?: PillTone;
  variant?: PillVariant;
}

const getToneStyles = (tone: PillTone, variant: PillVariant) => (
  theme: Theme
) => {
  const colors = {
    neutral: theme.palette.text.secondary,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    danger: theme.palette.error.main,
  };
  const color = colors[tone];

  if (variant === 'outlined') {
    return {
      color: tone === 'neutral' ? theme.palette.text.secondary : color,
      bgcolor: 'transparent',
      borderColor:
        tone === 'neutral' ? theme.palette.outline : alpha(color, 0.62),
    };
  }

  return {
    color: tone === 'neutral' ? theme.palette.text.primary : color,
    bgcolor: alpha(color, tone === 'neutral' ? 0.12 : 0.17),
    borderColor: 'transparent',
  };
};

const baseStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  border: '1px solid transparent',
  px: 1.25,
  borderRadius: 999,
  fontSize: '0.8125rem',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};
const roundedStyles = { borderRadius: 1 };

const Pill = React.memo(
  ({
    shape = 'pill',
    tone = 'neutral',
    variant = 'filled',
    sx,
    ...props
  }: PillProps) => {
    const combinedStyles = [
      baseStyles,
      shape === 'rounded' ? roundedStyles : null,
      getToneStyles(tone, variant),
      sx,
    ].filter(Boolean) as SxProps<Theme>;

    return <Box component="span" {...props} sx={combinedStyles} />;
  }
);

Pill.displayName = 'Pill';

export { Pill };
